// src/services/BiometricService.js
import { BaseService } from './BaseService';
import { NativeBiometric, BiometryType } from "@capgo/capacitor-native-biometric";
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { userService, authManager } from './index';
import { isCapacitor } from '../utils/platform';
import { tokenError, createLogger } from './Debug';
export class BiometricService extends BaseService {
  constructor() {
    super();
    this.isCapacitor = isCapacitor;
    this.SERVER_KEY = 'voxia.app'; // Server identifier for credentials
  }

  /**
   * Check if biometric authentication is available on the device
   */
  async isAvailable() {
    if (!this.isCapacitor) return false;
    
    try {
      const result = await NativeBiometric.isAvailable();
      return result.isAvailable;
    } catch (error) {
      tokenError('Error checking biometric availability:', error);
      return false;
    }
  }

  /**
   * Get available biometric type
   */
  async getBiometricType() {
    if (!this.isCapacitor) return null;
    
    try {
      const result = await NativeBiometric.isAvailable();
      if (!result.isAvailable) return null;
      
      switch (result.biometryType) {
        case BiometryType.FACE_ID:
          return 'Face ID';
        case BiometryType.TOUCH_ID:
          return 'Touch ID';
        case BiometryType.FINGERPRINT:
          return 'Empreinte digitale';
        case BiometryType.FACE_AUTHENTICATION:
          return 'Reconnaissance faciale';
        case BiometryType.IRIS_AUTHENTICATION:
          return 'Scan iris';
        default:
          return 'Biométrique';
      }
    } catch (error) {
      const log = createLogger('BiometricService.getBiometricType');  
      log.error('Error getting biometric type:', error);
      return null;
    }
  }

  /**
   * Get available biometric types as an array
   * Used by TrustDeviceModal to determine which biometric methods are available
   */
  async getAvailableTypes() {
    if (!this.isCapacitor) return [];
    
    try {
      const result = await NativeBiometric.isAvailable();
      if (!result.isAvailable) return [];
      
      const types = [];
      switch (result.biometryType) {
        case BiometryType.FACE_ID:
        case BiometryType.FACE_AUTHENTICATION:
          types.push('face');
          break;
        case BiometryType.TOUCH_ID:
        case BiometryType.FINGERPRINT:
          types.push('fingerprint');
          break;
        case BiometryType.IRIS_AUTHENTICATION:
          types.push('iris');
          break;
        default:
          // If we have biometry but can't determine the type, return generic
          if (result.isAvailable) {
            types.push('biometric');
          }
          break;
      }
      return types;
    } catch (error) {
      const log = createLogger('BiometricService.getAvailableTypes');
      log.error('Error getting available biometric types:', error);
      return [];
    }
  }

  // ===== NEW KEYPAIR-BASED TRUSTED DEVICE FLOW =====

  // Generate and store an ECDSA P-256 keypair; public key saved in Preferences (PEM), private key protected via biometric in keychain
  async generateDeviceKeypairIfMissing() {
    if (!this.isCapacitor) {
      throw new Error('Keypair generation is only available on mobile devices');
    }

    const log = createLogger('BiometricService.generateDeviceKeypairIfMissing');
    try {
      const { value: existingPubPem } = await Preferences.get({ key: 'device_public_key_pem' });
      const { value: hasPrivate } = await Preferences.get({ key: 'device_private_key_stored' });
      if (existingPubPem && hasPrivate === 'true') {
        return existingPubPem;
      }

      if (!window?.crypto?.subtle) {
        throw new Error('WebCrypto not available for key generation');
      }

      const keyPair = await window.crypto.subtle.generateKey(
        { name: 'ECDSA', namedCurve: 'P-256' },
        true,
        ['sign', 'verify']
      );

      // Export public key (SPKI) and private key (PKCS8)
      const spki = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
      const pkcs8 = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

      const publicKeyPem = this.arrayBufferToPem(spki, 'PUBLIC KEY');
      const privateKeyPem = this.arrayBufferToPem(pkcs8, 'PRIVATE KEY');

      // Store public key PEM in Preferences
      await Preferences.set({ key: 'device_public_key_pem', value: publicKeyPem });

      // Store private key PEM in keychain protected by biometric/user presence
      await NativeBiometric.setCredentials({
        username: 'device_private_key',
        password: privateKeyPem,
        server: 'com.gbmmedical.voxia.device'
      });

      await Preferences.set({ key: 'device_private_key_stored', value: 'true' });

      log.debug('Device keypair generated and stored');
      return publicKeyPem;
    } catch (error) {
      log.error('Error generating device keypair:', error);
      throw error;
    }
  }

  // Helper: convert ArrayBuffer DER to PEM
  arrayBufferToPem(buffer, label) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    const lineLength = 64;
    const lines = [];
    for (let i = 0; i < base64.length; i += lineLength) {
      lines.push(base64.slice(i, i + lineLength));
    }
    return `-----BEGIN ${label}-----\n${lines.join('\n')}\n-----END ${label}-----\n`;
  }

  // Retrieve private key PEM from keychain (prompts biometric), then import to CryptoKey
  async getPrivateKeyForSigning() {
    if (!this.isCapacitor) {
      throw new Error('Signing is only available on mobile devices');
    }
    const log = createLogger('BiometricService.getPrivateKeyForSigning');
    try {
      // Require user presence
      await this.authenticate('Autorisez la signature');

      const credentials = await NativeBiometric.getCredentials({ server: 'com.gbmmedical.voxia.device' });
      const privateKeyPem = credentials?.password;
      if (!privateKeyPem) {
        throw new Error('Device private key unavailable');
      }

      const pkcs8Der = this.pemToArrayBuffer(privateKeyPem);
      const privateKey = await window.crypto.subtle.importKey(
        'pkcs8',
        pkcs8Der,
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['sign']
      );
      return privateKey;
    } catch (error) {
      log.error('Error retrieving private key for signing:', error);
      throw error;
    }
  }

  // Helper: PEM to ArrayBuffer
  pemToArrayBuffer(pem) {
    const b64 = pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');
    const binaryString = atob(b64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // Sign arbitrary data (Uint8Array) with device key (prompts biometric)
  async sign(data) {
    const privateKey = await this.getPrivateKeyForSigning();
    const signature = await window.crypto.subtle.sign(
      { name: 'ECDSA', hash: { name: 'SHA-256' } },
      privateKey,
      data
    );
    return new Uint8Array(signature);
  }

  // Base64 encode Uint8Array
  toBase64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // Convert raw ECDSA (r||s) to DER SEQUENCE(INTEGER r, INTEGER s)
  ecdsaRawToDer(rawSig) {
    if (!(rawSig instanceof Uint8Array)) {
      rawSig = new Uint8Array(rawSig);
    }
    const n = rawSig.length / 2;
    const r = rawSig.slice(0, n);
    const s = rawSig.slice(n);

    const encodeInt = (arr) => {
      let i = 0;
      while (i < arr.length - 1 && arr[i] === 0) i++;
      let v = arr.slice(i);
      if (v[0] & 0x80) {
        const prefixed = new Uint8Array(v.length + 1);
        prefixed[0] = 0x00;
        prefixed.set(v, 1);
        v = prefixed;
      }
      const out = new Uint8Array(2 + v.length);
      out[0] = 0x02; // INTEGER
      out[1] = v.length;
      out.set(v, 2);
      return out;
    };

    const rEnc = encodeInt(r);
    const sEnc = encodeInt(s);
    const seqLen = rEnc.length + sEnc.length;
    const der = new Uint8Array(2 + seqLen);
    der[0] = 0x30; // SEQUENCE
    der[1] = seqLen;
    der.set(rEnc, 2);
    der.set(sEnc, 2 + rEnc.length);
    return der;
  }

  // Convert backend nonce to bytes with heuristics (base64 → hex → utf8)
  nonceToBytes(nonce) {
    if (!nonce) return new Uint8Array();
    const isBase64 = /^[A-Za-z0-9+/=]+$/.test(nonce) && nonce.length % 4 === 0;
    if (isBase64) {
      try {
        const bin = atob(nonce);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
      } catch (_) {}
    }
    const isHex = /^[0-9a-fA-F]+$/.test(nonce) && nonce.length % 2 === 0;
    if (isHex) {
      const out = new Uint8Array(nonce.length / 2);
      for (let i = 0; i < nonce.length; i += 2) {
        out[i / 2] = parseInt(nonce.substr(i, 2), 16);
      }
      return out;
    }
    // Fallback: UTF-8 encode
    const encoder = new TextEncoder();
    return encoder.encode(nonce);
  }

  // Decode base64url string to bytes
  base64UrlToBytes(b64u) {
    try {
      let b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64.length % 4;
      if (pad) b64 += '='.repeat(4 - pad);
      const bin = atob(b64);
      const out = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out;
    } catch (_) {
      // Fallback to UTF-8
      const encoder = new TextEncoder();
      return encoder.encode(String(b64u));
    }
  }

  // Full biometric device login flow: challenge → sign → verify → tokens in UserService
  async loginWithBiometricDevice() {
    if (!this.isCapacitor) {
      throw new Error('Biometric device login is only available on mobile devices');
    }
    const log = createLogger('BiometricService.loginWithBiometricDevice');
    try {

      // Resolve device identifiers
      const idInfo = await Device.getId();
      const deviceId = idInfo?.uuid || idInfo?.identifier || 'unknown-mobile-device';

      // Ensure keypair was previously enrolled; do NOT rotate keys during login
      const { value: pubPem } = await Preferences.get({ key: 'device_public_key_pem' });
      const { value: hasPriv } = await Preferences.get({ key: 'device_private_key_stored' });
      if (!pubPem || hasPriv !== 'true') {
        const log = createLogger('BiometricService.loginWithBiometricDevice');
        log.error('Missing enrolled device keys; please (re)enroll before biometric login', { hasPubKey: !!pubPem, hasPrivateFlag: hasPriv === 'true' });
        throw new Error('Cet appareil doit être (ré)enregistré avant la connexion biométrique');
      }

      // 1) Get challenge
      const challenge = await userService.getDeviceChallenge(deviceId);
      const nonce = challenge?.nonce || challenge?.data?.nonce;
      if (!nonce) throw new Error('No nonce in challenge');

      // 2) Sign the decoded bytes if nonce is base64url; otherwise UTF-8
      let nonceBytes;
      if (typeof nonce === 'string' && /^[A-Za-z0-9_-]+$/.test(nonce) && (nonce.length === 43 || nonce.length % 4 !== 0)) {
        nonceBytes = this.base64UrlToBytes(nonce);
      } else if (typeof nonce === 'string') {
        nonceBytes = this.nonceToBytes(nonce);
      } else {
        const encoder = new TextEncoder();
        nonceBytes = encoder.encode(String(nonce));
      }
      const signatureBytes = await this.sign(nonceBytes);
      const derSignature = this.ecdsaRawToDer(signatureBytes);
      const signatureB64 = this.toBase64(derSignature);

      try {
        log.debug('Trusted device verification payload (client)', {
          deviceId,
          nonceType: typeof nonce,
          nonceTextLen: typeof nonce === 'string' ? nonce.length : undefined,
          noncePreview: typeof nonce === 'string' ? `${nonce.slice(0, 24)}...` : undefined,
          nonceBytesLen: nonceBytes?.length,
          signatureB64Len: signatureB64.length,
          signatureB64Head: signatureB64.slice(0, 40),
          publicKeyExists: !!pubPem,
          publicKeyHead: pubPem?.slice(0, 60),
          publicKeyTail: pubPem?.slice(-60)
        });
      } catch (_) {}

      // 3) Verify and receive tokens
      const info = await Device.getInfo();
      const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
      const deviceInfo = {
        id: deviceId,
        name: info?.name || `${info?.manufacturer || ''} ${info?.model || ''}`.trim(),
        platform
      };

      const response = await authManager.loginWithTrustedDevice(deviceId, signatureB64, null, deviceInfo);
      log.debug('Biometric device login succeeded');
      return response;
    } catch (error) {
      log.error('Biometric device login failed:', error);
      throw error;
    }
  }

  // Enroll current device as trusted with server
  async enrollTrustedDevice(deviceName = null) {
    if (!this.isCapacitor) {
      throw new Error('Trusted device enrollment is only available on mobile devices');
    }

    const log = createLogger('BiometricService.enrollTrustedDevice');
    try {
      const info = await Device.getInfo();
      const idInfo = await Device.getId();
      const deviceId = idInfo?.uuid || idInfo?.identifier || 'unknown-mobile-device';
      const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';

      const publicKeyPem = await this.generateDeviceKeypairIfMissing();

      const payload = {
        deviceId,
        deviceName: deviceName || `${info.manufacturer} ${info.model}`,
        platform,
        publicKey: publicKeyPem
      };

      log.debug('Trusted device enrollment payload (client)', {
        deviceId: payload.deviceId,
        platform: payload.platform,
        deviceName: payload.deviceName,
        publicKeyLen: publicKeyPem?.length,
        publicKeyHead: publicKeyPem?.slice(0, 60),
        publicKeyTail: publicKeyPem?.slice(-60)
      });

      await userService.trustDevice(payload.deviceId, payload.deviceName, payload.platform, payload.publicKey);

      // Persist local trust metadata
      await Preferences.set({ key: 'device_trusted', value: 'true' });
      await Preferences.set({ key: 'device_name', value: payload.deviceName });
      await Preferences.set({ key: 'device_trusted_date', value: new Date().toISOString() });

      log.debug('Device enrolled as trusted');
      return { success: true };
    } catch (error) {
      log.error('Error enrolling trusted device:', error);
      throw error;
    }
  }

  /**
   * Check if device is trusted
   */
  async isDeviceTrusted() {
    if (!this.isCapacitor) return false;

    try {
      const { value } = await Preferences.get({ key: 'device_trusted' });
      return value === 'true';
    } catch (error) {
      const log = createLogger('BiometricService.isDeviceTrusted');
      log.error('Error checking device trust status:', error);
      return false;
    }
  }

  /**
   * Get list of trusted devices from server
   */
  async getTrustedDevices() {
    const { userService } = require('./index');
    return await userService.getTrustedDevices();
  }

  /**
   * Remove a trusted device
   */
  async removeTrustedDevice(deviceId) {
    const { userService } = require('./index');
    return await userService.removeTrustedDevice(deviceId);
  }

  /**
   * Perform biometric authentication using NativeBiometric.
   * This just verifies the user's identity, it doesn't log them in.
   */
  async authenticate(reason = 'Authentifiez-vous pour continuer') {
    if (!this.isCapacitor) {
      throw new Error('Biometric authentication is only available on mobile devices');
    }

    try {
      await NativeBiometric.verifyIdentity({
        reason: reason,
        title: 'Authentification',
        subtitle: 'Utilisez votre empreinte digitale ou Face ID',
        description: 'Authentifiez-vous pour accéder à votre compte',
        negativeButtonText: 'Annuler',
        useFallback: true,
        fallbackTitle: 'Utiliser le code',
        maxAttempts: 3
      });
      
      return true;
    } catch (error) {
      const log = createLogger('BiometricService.authenticate');
      log.error('Biometric authentication error:', error);
      
      // Handle specific error codes
      if (error.code === '10') {
        throw new Error('Authentification échouée');
      } else if (error.code === '16') {
        throw new Error('Authentification annulée');
      } else if (error.code === '3') {
        throw new Error('Aucune donnée biométrique enregistrée sur cet appareil');
      }
      
      return false;
    }
  }

  // Deprecated legacy API (kept to avoid breaking imports). No longer used for auth.
  async performBiometricLogin() {
    throw new Error('Biometric password login is no longer supported');
  }


  /**
   * Save user credentials for biometric login using NativeBiometric
   */
  async saveCredentialsForBiometric() {
    // Repurposed: ensure device keypair exists and mark biometric enabled
    if (!this.isCapacitor) return;
    await this.generateDeviceKeypairIfMissing();
    await Preferences.set({ key: 'biometric_enabled', value: 'true' });
  }

  /**
   * Get saved credentials for biometric login using NativeBiometric
   */
  async getStoredCredentials() {
    // No longer applicable
      return null;
  }


  /**
   * Check if credentials exist without requiring authentication
   */
  async hasStoredCredentials() {
    if (!this.isCapacitor) return false;
    try {
      const { value } = await Preferences.get({ key: 'device_private_key_stored' });
      return value === 'true';
    } catch (error) {
      const log = createLogger('BiometricService.hasStoredCredentials');
      log.error('Error checking device key presence:', error);
      return false;
    }
  }

  /**
   * Enable biometric login (just the setting, not the credentials)
   */
  async enableBiometric() {
    if (!this.isCapacitor) return;
    
    try {
      await Preferences.set({ 
        key: 'biometric_enabled', 
        value: 'true' 
      });
    } catch (error) {
      const log = createLogger('BiometricService.enableBiometric');
      log.error('Error enabling biometric:', error);
      throw error;
    }
  }

  /**
   * Disable biometric login
   */
  async disableBiometric() {
    if (!this.isCapacitor) return;
    
    try {
      // Best-effort: do not delete device keypair automatically; only disable flag
      await Preferences.set({ key: 'biometric_enabled', value: 'false' });
    } catch (error) {
      const log = createLogger('BiometricService.disableBiometric');
        log.error('Error disabling biometric:', error);
      throw error;
    }
  }

  /**
   * Check biometric error and return user-friendly message
   */
  getBiometricErrorMessage(errorCode) {
    const log = createLogger('BiometricService.getBiometricErrorMessage');
    const errorMessages = {
      '0': 'Erreur inconnue',
      '1': 'Biométrie non disponible',
      '2': 'Trop de tentatives échouées',
      '3': 'Aucune donnée biométrique enregistrée',
      '4': 'Veuillez réessayer dans 30 secondes',
      '10': 'Authentification échouée',
      '11': 'Annulé par l\'application',
      '12': 'Contexte invalide',
      '13': 'Interaction non disponible',
      '14': 'Code d\'accès non configuré',
      '15': 'Annulé par le système',
      '16': 'Annulé par l\'utilisateur',
      '17': 'Utilisation du code d\'accès'
    };
    
    return errorMessages[errorCode?.toString()] || 'Erreur d\'authentification';
  }
}